import React, { useState, useRef, useEffect } from 'react';
import { Play, Volume2, VolumeX, Maximize, RotateCcw } from 'lucide-react';
import { Dialog, DialogContent } from '../ui/dialog';
import useVideoPosterFrame from './useVideoPosterFrame';

// Lightbox-style video player for the LaunchPad welcome video.
// Marks the video as "watched" once playback reaches 80% (or onEnded), so we
// can flip the FAB / quickstart task to its "Rewatch" affordance.
const WelcomeVideoDialog = ({
  open,
  onOpenChange,
  onWatched,
  thumbnailUrl,
  videoSrc,
  title = 'Welcome',
  duration = null,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const videoRef = useRef(null);
  // Caller can supply `thumbnailUrl` as an explicit override; otherwise we
  // capture frame 20 of the video as the poster.
  const extractedPoster = useVideoPosterFrame(thumbnailUrl ? null : videoSrc);
  const effectivePoster = thumbnailUrl || extractedPoster || undefined;

  // Reset transient state when the dialog closes.
  useEffect(() => {
    if (!open) {
      setIsPlaying(false);
      setHasStarted(false);
      setProgress(0);
    }
  }, [open]);

  const handlePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
        if (!hasStarted) setHasStarted(true);
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (!videoRef.current) return;
    const pct = (videoRef.current.currentTime / videoRef.current.duration) * 100;
    setProgress(pct);
    if (pct >= 80) onWatched?.();
  };

  const handleRestart = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play();
      setIsPlaying(true);
    }
    setProgress(0);
  };

  const toggleMute = () => {
    const next = !isMuted;
    setIsMuted(next);
    if (videoRef.current) videoRef.current.muted = next;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Inline transform centers the panel — the project's Tailwind config
          doesn't compile the `-translate-x-1/2 -translate-y-1/2` utilities the
          primitive relies on, so the computed transform is `none` and the
          panel lands bottom-right. Inline style is config-independent. */}
      <DialogContent
        className="max-w-3xl p-0 overflow-hidden bg-black border-0 gap-0"
        style={{ transform: 'translate(-50%, -50%)' }}
      >
        {/* Video area */}
        <div className="relative aspect-video bg-black group">
          {videoSrc && (
            <video
              ref={videoRef}
              src={videoSrc}
              poster={effectivePoster}
              preload="metadata"
              playsInline
              className="w-full h-full object-cover"
              muted={isMuted}
              onTimeUpdate={handleTimeUpdate}
              onEnded={() => { setIsPlaying(false); onWatched?.(); }}
            />
          )}

          {/* Thumbnail overlay before play */}
          {thumbnailUrl && !hasStarted && (
            <img
              src={thumbnailUrl}
              alt="Video thumbnail"
              className="absolute inset-0 w-full h-full object-cover"
            />
          )}

          {/* Big Play button — shown until playback starts */}
          {!isPlaying && (
            <button
              type="button"
              onClick={handlePlay}
              className="absolute inset-0 flex items-center justify-center group/play"
              aria-label="Play video"
            >
              <div className="w-20 h-20 rounded-full bg-white/15 backdrop-blur-sm border border-white/30 flex items-center justify-center group-hover/play:scale-110 group-hover/play:bg-white/25 transition-all duration-200 shadow-2xl">
                <Play className="w-9 h-9 text-white fill-white ml-1" />
              </div>
            </button>
          )}

          {/* Hover controls — only after playback has started */}
          {hasStarted && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <div className="mb-3 h-1 bg-white/20 rounded-full overflow-hidden">
                <div
                  className="h-full bg-succession-primary rounded-full transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handlePlay}
                    className="text-white hover:text-succession-primary transition-colors"
                    aria-label={isPlaying ? 'Pause' : 'Play'}
                  >
                    {isPlaying ? (
                      <div className="flex gap-1">
                        <div className="w-1 h-4 bg-current rounded-sm" />
                        <div className="w-1 h-4 bg-current rounded-sm" />
                      </div>
                    ) : (
                      <Play className="w-5 h-5 fill-current" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={toggleMute}
                    className="text-white hover:text-succession-primary transition-colors"
                    aria-label={isMuted ? 'Unmute' : 'Mute'}
                  >
                    {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                  </button>
                  <button
                    type="button"
                    onClick={handleRestart}
                    className="text-white hover:text-succession-primary transition-colors"
                    aria-label="Restart"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex items-center gap-2 text-white/70 text-xs">
                  {duration && <span>{duration}</span>}
                  <Maximize className="w-4 h-4" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Below-video info bar */}
        <div className="bg-succession-bg-secondary border-t border-succession-border px-6 py-4 flex items-center justify-between">
          <div>
            <h4 className="text-succession-text-primary font-semibold text-sm">{title}</h4>
            <p className="text-succession-text-muted text-xs mt-0.5">
              {duration ? `${duration} · ` : ''}Platform overview
            </p>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="text-succession-text-muted hover:text-succession-text-primary transition-colors text-sm"
          >
            Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WelcomeVideoDialog;
